// ./src/App.jsx
import React, { useState } from "react";
import Header from "./components/Header";
import Upload from "./components/Upload";
import TranscriptDisplay from "./components/TranscriptDisplay";
import { Sparkles, Zap, Target, Users } from "lucide-react";
import "./index.css";

function App() {
  const [currentTranscript, setCurrentTranscript] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  const handleUploadStart = () => {
    setIsProcessing(true);
    setCurrentTranscript(null);
  };

  const handleTranscriptReceived = (transcript) => {
    setCurrentTranscript(transcript);
    setIsProcessing(false);
  };

  const handleUploadError = () => {
    setIsProcessing(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-neutral-50 via-white to-neutral-100">
      <Header />

      <main className="container mx-auto px-6 py-12 max-w-5xl">
        {/* Hero Section */}
        <div className="text-center mb-12 animate-fadeInUp">
          <div className="flex items-center justify-center space-x-2 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-primary-600 rounded-2xl flex items-center justify-center shadow-color animate-float">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gradient">
              Meeting Transkription
            </h1>
            <div
              className="w-12 h-12 bg-gradient-to-br from-emerald-500 to-cyan-500 rounded-2xl flex items-center justify-center shadow-color animate-float"
              style={{ animationDelay: "0.5s" }}
            >
              <Zap className="w-6 h-6 text-white" />
            </div>
          </div>

          <p className="text-xl text-neutral-600 max-w-3xl mx-auto leading-relaxed mb-8">
            Lade deine Meeting-Aufzeichnung hoch und erhalte eine automatische,
            <span className="text-gradient font-semibold">
              {" "}
              KI-gestützte Transkription
            </span>{" "}
            in Sekunden
          </p>

          {/* Feature Highlights */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto mb-12">
            <div className="group">
              <div className="card-modern p-6 text-center hover-lift group-hover:shadow-color transition-all duration-500">
                <div className="w-14 h-14 bg-gradient-to-br from-purple-100 to-purple-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Target className="w-7 h-7 text-purple-600" />
                </div>
                <h3 className="font-bold text-neutral-800 mb-2">
                  99% Genauigkeit
                </h3>
                <p className="text-neutral-600 text-sm">
                  Modernste KI für präzise Spracherkennung
                </p>
              </div>
            </div>

            <div className="group">
              <div className="card-modern p-6 text-center hover-lift group-hover:shadow-color transition-all duration-500">
                <div className="w-14 h-14 bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Zap className="w-7 h-7 text-emerald-600" />
                </div>
                <h3 className="font-bold text-neutral-800 mb-2">
                  Blitzschnell
                </h3>
                <p className="text-neutral-600 text-sm">
                  Ergebnisse in unter 5 Minuten
                </p>
              </div>
            </div>

            <div className="group">
              <div className="card-modern p-6 text-center hover-lift group-hover:shadow-color transition-all duration-500">
                <div className="w-14 h-14 bg-gradient-to-br from-cyan-100 to-cyan-200 rounded-2xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300">
                  <Users className="w-7 h-7 text-cyan-600" />
                </div>
                <h3 className="font-bold text-neutral-800 mb-2">Team-Ready</h3>
                <p className="text-neutral-600 text-sm">
                  Perfekt für alle Meeting-Formate
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="space-y-12">
          <Upload
            onUploadStart={handleUploadStart}
            onTranscriptReceived={handleTranscriptReceived}
            onError={handleUploadError}
            isProcessing={isProcessing}
          />

          {(currentTranscript || isProcessing) && (
            <TranscriptDisplay
              transcript={currentTranscript}
              isProcessing={isProcessing}
            />
          )}
        </div>

        {/* Bottom CTA */}
        {!isProcessing && !currentTranscript && (
          <div className="mt-16 text-center animate-fadeInUp">
            <div className="card-premium p-8 max-w-2xl mx-auto">
              <div className="flex items-center justify-center space-x-3 mb-4">
                <Sparkles className="w-6 h-6 text-primary-600 animate-pulse" />
                <h3 className="text-xl font-bold text-neutral-800">
                  Bereit für dein erstes Meeting?
                </h3>
                <Sparkles
                  className="w-6 h-6 text-emerald-600 animate-pulse"
                  style={{ animationDelay: "0.5s" }}
                />
              </div>
              <p className="text-neutral-600 mb-6">
                Probiere unsere KI-Transkription kostenlos aus und erlebe, wie
                einfach Meeting-Dokumentation sein kann.
              </p>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm text-neutral-500">
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                  <span>Kostenlos testen</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                  <span>Keine Anmeldung</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-cyan-500 rounded-full"></div>
                  <span>Datenschutz sicher</span>
                </div>
                <div className="flex items-center space-x-2">
                  <div className="w-2 h-2 bg-orange-500 rounded-full"></div>
                  <span>Sofort verfügbar</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      {/* Background Decorations */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-purple-400/20 to-pink-400/20 rounded-full blur-3xl animate-pulse"></div>
        <div
          className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-emerald-400/20 to-cyan-400/20 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "2s" }}
        ></div>
        <div
          className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-96 h-96 bg-gradient-to-br from-orange-400/10 to-yellow-400/10 rounded-full blur-3xl animate-pulse"
          style={{ animationDelay: "4s" }}
        ></div>
      </div>
    </div>
  );
}

export default App;
